import { BuilderTemplateId } from '@deltasierra/ids';
import { useControlledSearchInput } from '@deltasierra/react-hooks';
import { isNotNullOrUndefined } from '@deltasierra/type-utilities';
import * as React from 'react';
import { ValidationState } from '../values/validation-state.enum';
import { useTemplateIdFromUrl } from './use-template-id-from-url';
import { ParsedTemplateId, useTemplateIdState } from './use-template-id-state';
import { useUpdateUrlForValidTemplate } from './use-update-url-for-valid-template';
import { useValidatedTemplate } from './use-validated-template';
import { ValidatedTemplate } from './validated-template';

type InitialIds = {
    buildableTemplateId: string | null;
    templateId: BuilderTemplateId | null;
};

function getInitialParsedId({ buildableTemplateId, templateId }: InitialIds): ParsedTemplateId {
    if (isNotNullOrUndefined(templateId)) {
        return { id: templateId, type: 'builder-template' };
    }
    if (isNotNullOrUndefined(buildableTemplateId)) {
        return { id: buildableTemplateId, type: 'buildable-template' };
    }
    return { type: 'empty' };
}

type UseTemplateSelectOptions = InitialIds & {
    clientId: string | null;
    locationId: string | null;
    onChange?: (value: ValidatedTemplate | null) => void;
};

type UseTemplateSelectReturns = [
    inputProps: { onChange: React.ChangeEventHandler<HTMLInputElement>; value: string },
    state: ValidationState,
    thumbnail?: string,
];

/**
 * I am not proud of any of this. This entire hook is a hack around a terribly designed
 * component and state management/fetching logic combination. To fix the problems in here
 * I would have to rewrite the entire component and all of its children, which I don't have time for
 * so instead I have tried to clean it up.
 */
export function useTemplateSelect({
    buildableTemplateId,
    clientId,
    locationId,
    onChange,
    templateId,
}: UseTemplateSelectOptions): UseTemplateSelectReturns {
    const onChangeRef = React.useRef(onChange);

    // This is the actual set state from the parent
    const [parsedId, setParsedId] = useTemplateIdState(getInitialParsedId({ buildableTemplateId, templateId }));

    // This is hacks on hacks to avoid setting the parsedId to null on the first render
    const [firstRender, setFirstRender] = React.useState(true);

    const {
        inputProps,
        onChange: setUrl,
        searchTerm,
    } = useControlledSearchInput({
        initialValue: '',
        interval: 300,
    });

    const urlParsedId = useTemplateIdFromUrl(searchTerm);

    // If the input from the parent changes we need to update it in the state
    React.useEffect(() => {
        const newParsedId = getInitialParsedId({ buildableTemplateId, templateId });
        // Only set the new parsed id if it has actually changed
        if (JSON.stringify(newParsedId) === JSON.stringify(parsedId)) {
            return;
        }
        setParsedId(newParsedId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buildableTemplateId, templateId, setParsedId]);

    // If the urlParsedId changes we need to update it in the state
    React.useEffect(() => {
        if (firstRender) {
            setFirstRender(false);
            return;
        }
        // Only set the new state id if it has actually changed
        if (JSON.stringify(urlParsedId) === JSON.stringify(parsedId)) {
            return;
        }
        setParsedId(urlParsedId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlParsedId, setParsedId]);

    const { loading, template } = useValidatedTemplate(parsedId);

    useUpdateUrlForValidTemplate({
        clientId,
        locationId,
        setUrl,
        template,
    });

    // Update the parent value when the value is loaded
    React.useEffect(() => {
        if (loading) {
            return;
        }

        if (template) {
            onChangeRef.current?.(template);
        } else {
            onChangeRef.current?.(null);
        }
    }, [template, loading]);

    const validation: ValidationState = React.useMemo(() => {
        if (loading) {
            return ValidationState.Validating;
        }
        if (parsedId.type === 'empty') {
            return ValidationState.None;
        }
        if (template) {
            return ValidationState.Valid;
        }
        return ValidationState.Invalid;
    }, [loading, parsedId, template]);

    return [inputProps, validation, template?.thumbnail];
}
